import { useEffect } from 'react';
import { useQueryClient, QueryClient } from '@tanstack/react-query';
import { SignalRService } from '../utils/signalrService';
import { ContentsClient, MessageCreatedEventData, MessageUpdatedEventData, MessageDeletedEventData, MemberAddedEventData, MemberRemovedEventData, UserAddedToThreadEventData, UserRemovedFromThreadEventData } from '../api/api-client';
import { CryptoHelper } from '../utils/cryptoHelper';
import { DecryptedThread, DecryptedContentVersionDto } from './useThread';

/**
 * Sort messages using the same logic as the server:
 * 1. Completed messages first (CompletedAt.HasValue ? 0 : 1)
 * 2. Then by completion time (CompletedAt)
 * 3. Then submitted messages (SubmittedAt.HasValue ? 0 : 1)  
 * 4. Then by submission time (SubmittedAt)
 * 5. Finally drafts by started time (StartedAt)
 */
const sortMessages = (messages: DecryptedContentVersionDto[]): DecryptedContentVersionDto[] => {
  return messages.sort((a, b) => {
    // 1. Completed messages first (CompletedAt.HasValue ? 0 : 1)
    const aCompleted = a.completedAt ? 0 : 1;
    const bCompleted = b.completedAt ? 0 : 1;
    if (aCompleted !== bCompleted) return aCompleted - bCompleted;
    
    // 2. Then by completion time (CompletedAt)
    if (a.completedAt && b.completedAt) {
      const aTime = new Date(a.completedAt).getTime();
      const bTime = new Date(b.completedAt).getTime();
      if (aTime !== bTime) return aTime - bTime;
    }
    
    // 3. Then submitted messages (SubmittedAt.HasValue ? 0 : 1)
    const aSubmitted = a.submittedAt ? 0 : 1;
    const bSubmitted = b.submittedAt ? 0 : 1;
    if (aSubmitted !== bSubmitted) return aSubmitted - bSubmitted;
    
    // 4. Then by submission time (SubmittedAt)
    if (a.submittedAt && b.submittedAt) {
      const aTime = new Date(a.submittedAt).getTime();
      const bTime = new Date(b.submittedAt).getTime();
      if (aTime !== bTime) return aTime - bTime;
    }
    
    // 5. Finally drafts by started time (StartedAt)
    const aStarted = new Date(a.startedAt || new Date()).getTime();
    const bStarted = new Date(b.startedAt || new Date()).getTime();
    return aStarted - bStarted;
  });
};

/**
 * Check if we should fetch and update the cache vs just invalidate for later
 * Returns { shouldFetchAndUpdate: boolean, threadData?: DecryptedThread }
 * 
 * We only fetch+update if:
 * - There's an active observer (someone viewing the thread)
 * - We have cached data with the decryption key
 * Otherwise we just invalidate and let TanStack Query handle it when needed
 */
const checkThreadCacheStrategy = (queryClient: QueryClient, threadId: number) => {
  const queryState = queryClient.getQueryState(['thread', threadId]);
  
  // If no query state exists, just invalidate
  if (!queryState) {
    return { shouldFetchAndUpdate: false };
  }

  // Check if there are active observers by looking at observer count
  const query = queryClient.getQueryCache().find({ queryKey: ['thread', threadId] });
  const observerCount = query?.getObserversCount() || 0;
  
  if (!query || observerCount === 0) {
    return { shouldFetchAndUpdate: false };
  }

  const threadData = queryClient.getQueryData<DecryptedThread>(['thread', threadId]);
  
  if (!threadData?.contentKey) {
    return { shouldFetchAndUpdate: false };
  }

  return { shouldFetchAndUpdate: true, threadData };
};

/**
 * Fetch a message, decrypt it, and update the cache
 * Used by both MessageCreated and MessageUpdated events
 */
const fetchAndUpdateMessage = async (
  queryClient: QueryClient, 
  threadId: number, 
  messageId: number, 
  threadData: DecryptedThread,
  isUpdate: boolean = false
) => {
  try {
    // Fetch and decrypt the message
    const contentsClient = new ContentsClient();
    const messageData = await contentsClient.getMessage(threadId, messageId);
    
    if (!messageData.encryptedData) {
      throw new Error('Message has no encrypted data');
    }
    
    const decryptedContent = await CryptoHelper.decryptContent(messageData.encryptedData, threadData.contentKey!);
    
    const updatedMessage: DecryptedContentVersionDto = {
      ...messageData,
      decryptedData: decryptedContent
    };

    // Update cache
    queryClient.setQueryData<DecryptedThread>(['thread', threadId], (oldData) => {
      if (!oldData) return oldData;
      
      let updatedMessages: DecryptedContentVersionDto[];
      
      if (isUpdate) {
        // Replace existing message
        updatedMessages = (oldData.decryptedMessages || []).map(msg => 
          msg.id === messageId ? updatedMessage : msg
        );
      } else {
        // Add new message
        updatedMessages = [...(oldData.decryptedMessages || []), updatedMessage];
      }
      
      return { ...oldData, decryptedMessages: sortMessages(updatedMessages) };
    });
  } catch (error) {
    console.warn(`Failed to fetch ${isUpdate ? 'updated' : 'new'} message, falling back to full refresh:`, error);
    queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
  }
};

/**
 * Centralized hook that connects SignalR events to TanStack Query cache invalidation
 * This eliminates the need for individual components to handle SignalR listeners
 * 
 * ARCHITECTURE DECISION: SignalR invalidation strategy
 * - We use simple queryClient.invalidateQueries() calls without refetchType restrictions
 * - TanStack Query's built-in observer system automatically handles:
 *   * Only fetching when components are actively observing the data
 *   * Marking as stale (no fetch) when no active observers
 *   * Providing real-time updates when users are viewing data
 *   * Optimizing bandwidth by not fetching unused data
 * - This gives us the best of both worlds: live updates + performance
 * - DON'T add manual observer checks or refetchType:'none' - TanStack Query handles this
 */
export const useSignalRSync = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Content Events
    const handleContentCreated = async (contentId: number) => {
      // Check if there are active observers for the contents list
      const contentsQuery = queryClient.getQueryCache().find({ queryKey: ['contents'] });
      const observerCount = contentsQuery?.getObserversCount() || 0;
      
      // Only fetch if someone is actively viewing the contents list
      if (observerCount === 0) {
        queryClient.invalidateQueries({ queryKey: ['contents'] });
        return;
      }
      
      try {
        // Fetch the new content item and add it to cache optimistically
        const contentsClient = new ContentsClient();
        const newContent = await contentsClient.getContent(contentId);
        
        // Add to the contents list cache
        queryClient.setQueryData(['contents'], (oldData: any) => {
          if (!oldData) return [newContent];
          return [newContent, ...oldData];
        });
        
        // Also set the individual content cache
        queryClient.setQueryData(['content', contentId], newContent);
        
      } catch (error) {
        console.warn('Failed to fetch new content optimistically, falling back to invalidation:', error);
        // Fallback to invalidation if the fetch fails
        queryClient.invalidateQueries({ queryKey: ['contents'] });
      }
    };

    const handleContentUpdated = (contentId: number) => {
      // Content events are for thread metadata changes (title, permissions)
      queryClient.invalidateQueries({ queryKey: ['contents'] }); // Sidebar list
      queryClient.invalidateQueries({ queryKey: ['thread', contentId] }); // Thread details
    };

    const handleContentDeleted = (contentId: number) => {
      // Remove from all caches
      queryClient.removeQueries({ queryKey: ['contents', contentId] });
      queryClient.removeQueries({ queryKey: ['thread', contentId] });
      
      // Remove from sidebar list optimistically
      queryClient.setQueryData(['contents'], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter((content: any) => content.id !== contentId);
      });
    };

    // Message-specific events with smart cache updates
    const handleMessageCreated = async (data: MessageCreatedEventData) => {
      const threadId = data.threadId!;
      const messageId = data.messageId!;
      
      // Use optimized cache strategy
      const { shouldFetchAndUpdate, threadData } = checkThreadCacheStrategy(queryClient, threadId);
      
      if (!shouldFetchAndUpdate) {
        queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
        return;
      }

      await fetchAndUpdateMessage(queryClient, threadId, messageId, threadData!, false);
    };

    const handleMessageUpdated = async (data: MessageUpdatedEventData) => {
      const threadId = data.threadId!;
      const messageId = data.messageId!;
      
      // Use optimized cache strategy
      const { shouldFetchAndUpdate, threadData } = checkThreadCacheStrategy(queryClient, threadId);
      
      if (!shouldFetchAndUpdate) {
        queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
        return;
      }

      await fetchAndUpdateMessage(queryClient, threadId, messageId, threadData!, true);
    };

    const handleMessageDeleted = (data: MessageDeletedEventData) => {
      const threadId = data.threadId!;
      const messageId = data.messageId!;
      
      // For deletions, we can always do optimistic updates since no API call needed
      queryClient.setQueryData<DecryptedThread>(['thread', threadId], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          decryptedMessages: (oldData.decryptedMessages || []).filter(msg => msg.id !== messageId)
        };
      });
      
      // Also invalidate in case there's no cached data
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
    };

    // Legacy thread updated event (keep for backward compatibility)
    const handleThreadUpdated = (threadId: number) => {
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
    };

    // User/Account Events
    const handleUserUpdated = (userId: number) => {
      queryClient.invalidateQueries({ queryKey: ['account', 'members'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    };

    const handleMemberAdded = (data: MemberAddedEventData) => {
      queryClient.invalidateQueries({ queryKey: ['account', 'members'] });
    };

    const handleMemberRemoved = (data: MemberRemovedEventData) => {
      queryClient.invalidateQueries({ queryKey: ['account', 'members'] });
    };

    // Thread Member Events  
    const handleUserAddedToThread = (data: UserAddedToThreadEventData) => {
      const threadId = data.threadId!;
      // Invalidate thread data as member list affects permissions/access
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
    };

    const handleUserRemovedFromThread = (data: UserRemovedFromThreadEventData) => {
      const threadId = data.threadId!;
      // Invalidate thread data as member list affects permissions/access
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
    };

    // Team Events
    const handleTeamUpdated = (teamId: number) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['teams', teamId] });
    };

    // Add all SignalR listeners
    SignalRService.addListener('ContentCreated', handleContentCreated);
    SignalRService.addListener('ContentUpdated', handleContentUpdated);
    SignalRService.addListener('ContentDeleted', handleContentDeleted);
    SignalRService.addListener('UserUpdated', handleUserUpdated);
    SignalRService.addListener('MemberAdded', handleMemberAdded);
    SignalRService.addListener('MemberRemoved', handleMemberRemoved);
    SignalRService.addListener('UserAddedToThread', handleUserAddedToThread);
    SignalRService.addListener('UserRemovedFromThread', handleUserRemovedFromThread);
    SignalRService.addListener('TeamUpdated', handleTeamUpdated);
    
    // New granular message events
    SignalRService.addListener('MessageCreated', handleMessageCreated);
    SignalRService.addListener('MessageUpdated', handleMessageUpdated);
    SignalRService.addListener('MessageDeleted', handleMessageDeleted);
    
    // Legacy thread updated event (for backward compatibility)
    SignalRService.addListener('ThreadUpdated', handleThreadUpdated);

    // Cleanup function
    return () => {
      SignalRService.removeListener('ContentCreated', handleContentCreated);
      SignalRService.removeListener('ContentUpdated', handleContentUpdated);
      SignalRService.removeListener('ContentDeleted', handleContentDeleted);
      SignalRService.removeListener('UserUpdated', handleUserUpdated);
      SignalRService.removeListener('MemberAdded', handleMemberAdded);
      SignalRService.removeListener('MemberRemoved', handleMemberRemoved);
      SignalRService.removeListener('UserAddedToThread', handleUserAddedToThread);
      SignalRService.removeListener('UserRemovedFromThread', handleUserRemovedFromThread);
      SignalRService.removeListener('TeamUpdated', handleTeamUpdated);
      
      // Remove granular message event listeners
      SignalRService.removeListener('MessageCreated', handleMessageCreated);
      SignalRService.removeListener('MessageUpdated', handleMessageUpdated);
      SignalRService.removeListener('MessageDeleted', handleMessageDeleted);
      
      // Remove legacy thread updated listener
      SignalRService.removeListener('ThreadUpdated', handleThreadUpdated);
    };
  }, [queryClient]);
};